import { sortByDateThenNumber } from "./invoiceSorting";

/**
 * Series de numeración del libro de IVA de recibidas.
 * Cada factura puede llevar su propia serie (`seriesPrefix` + `seriesStart`);
 * si no, pertenece a la serie principal de la sesión. Cada serie se numera
 * de forma correlativa e independiente por fecha (y nº de factura en empate).
 */
export interface SeriesCarrier {
  id: string;
  date?: string | null;
  invoiceNumber?: string | null;
  seriesPrefix?: string;
  seriesStart?: number;
}

export interface SeriesInfo {
  prefix: string;
  start: number;
  count: number;
  isMain: boolean;
}

const norm = (p?: string | null) => (p ?? "").toString().trim().toUpperCase();

export const seriesKeyOf = (f: SeriesCarrier, mainPrefix: string): string => {
  const own = norm(f.seriesPrefix);
  return f.seriesPrefix !== undefined && own !== norm(mainPrefix) ? own : norm(mainPrefix);
};

export const listSeries = <T extends SeriesCarrier>(files: T[], mainPrefix: string, mainStart: number): SeriesInfo[] => {
  const main = norm(mainPrefix);
  const map = new Map<string, SeriesInfo>();
  map.set(main, { prefix: main, start: Math.max(1, mainStart || 1), count: 0, isMain: true });
  for (const f of files) {
    if (!f) continue;
    const key = seriesKeyOf(f, mainPrefix);
    let info = map.get(key);
    if (!info) {
      info = { prefix: key, start: Math.max(1, f.seriesStart || 1), count: 0, isMain: false };
      map.set(key, info);
    } else if (!info.isMain && f.seriesStart && f.seriesStart < info.start) {
      info.start = f.seriesStart;
    }
    info.count += 1;
  }
  return [...map.values()];
};

/** Devuelve id → número de registro de IVA (p. ej. "B12"). */
export const computeSeriesNumbers = <T extends SeriesCarrier>(
  files: T[],
  mainPrefix: string,
  mainStart: number,
): Map<string, string> => {
  const series = listSeries(files, mainPrefix, mainStart);
  const starts = new Map(series.map((s) => [s.prefix, s.start]));
  const counters = new Map<string, number>();
  const out = new Map<string, string>();
  for (const f of sortByDateThenNumber(files)) {
    const key = seriesKeyOf(f, mainPrefix);
    const n = counters.get(key) ?? 0;
    counters.set(key, n + 1);
    out.set(f.id, `${key}${(starts.get(key) ?? 1) + n}`);
  }
  return out;
};
